/**
 * <ColumnFilterEditor> — the column ⋮ menu's filter, in the control kit (#959).
 *
 * ── the seam, and why it is drawn here ────────────────────────────────────
 * `<DataGrid>` hands the column menu to the host as a render prop, so before
 * this file the ONLY column menu in the product was `apps/console`'s — written
 * inline in `ListView`, reachable by nothing else. Under #964's standing rule
 * ("where the shared component cannot do what a host needs, extend the shared
 * component — never fork it") the customer portal's only route to the same
 * experience was to reimplement it, which is the fork that rule exists to stop.
 *
 * So the FILTER EDITOR moves and the HOST's menu does not:
 *
 *   here          the operator set (typed by the column's kind), the typed value
 *                 inputs, the applied-state summary, Apply and Clear, and every
 *                 keyboard and announcement contract they carry
 *   the host      where a filter is STORED (the console mutates a saved view;
 *                 the portal may not have one), the popover it sits in, its
 *                 curated presets ("Overdue", "Mine" — shortcuts no generic
 *                 operator UI can express, and deliberately kept), the sort
 *                 items, hide/add column, and the Lookup behind a `ref` value
 *
 * The acceptance test for the split is the one `DataGrid` already passes: this
 * file takes NO data dependency. It imports three controls and a stylesheet. A
 * column's type, a status machine's states and a reference's label all arrive as
 * props, because resolving them is a READ and reads belong to the app.
 *
 * ── accessibility is the feature, not a pass over it ──────────────────────
 * This is a popover with form controls in it — the shape that is easiest to ship
 * mouse-only and hardest to notice. So:
 *   · every control has a real `<label htmlFor>`; ids are namespaced per column
 *     so two open menus can never collide
 *   · the operator is a native `<select>`. Deliberately not the kit's Radix
 *     `<Select>`: that portals its listbox to `document.body`, and the host's
 *     popover closes on any pointerdown outside its own subtree — so choosing an
 *     operator would close the menu you were choosing it in. A native select is
 *     also the one control every AT and every platform already knows
 *   · the applied state is a `role="status"` line in words, so it is ANNOUNCED
 *     and not merely tinted (the header's filtered mark was colour-only)
 *   · Enter in any value input applies, Apply is a real `<button type="button">`
 *     so it cannot submit a surrounding form, and the host returns focus to the
 *     ⋮ on close
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Button } from '../controls/Button'
import { Input } from '../controls/Input'
import { DatePicker } from '../controls/DatePicker'
import { MultiSelect } from '../controls/MultiSelect'
import {
  operatorsFor, conservativeReason, emptyDraft, draftIsComplete,
  encodeColumnFilter, decodeColumnFilter, summarizeColumnFilter,
  type ColumnFilterDraft, type ColumnFilterField, type ColumnFilterLeaf,
} from './columnFilter'
import s from './columnFilter.module.css'

export interface ColumnFilterEditorProps {
  /** The column, as the host resolved it from the metamodel. */
  field: ColumnFilterField
  /** EVERY leaf currently on the view — the editor reads only its own column's. */
  filters: ColumnFilterLeaf[]
  /**
   * Replace this column's leaves with these. The host decides what that MEANS —
   * the console forks the saved view rather than mutating a shared one.
   */
  onApply: (leaves: ColumnFilterLeaf[]) => void
  /** Remove every leaf on this column. Offered only when there is one. */
  onClear: () => void
  /** Applying or clearing closes the menu; the host owns the popover. */
  onDone?: () => void
  /**
   * A `ref` column's value picker — the host brings the data source, because a
   * reference is unbounded and resolving one is a read.
   */
  renderRefValue?: (a: {
    /** the currently chosen id, or '' */
    value: string
    onChange: (id: string, label?: string) => void
    /** put this on the focusable node so the editor's <label> names it */
    id: string
  }) => ReactNode
  /** Display label for a stored value (a ref id, mostly) — used in the summary. */
  labelOfValue?: (value: unknown) => string | undefined
  /** Namespace for the generated control ids. Defaults to the column key. */
  idPrefix?: string
}

export function ColumnFilterEditor({
  field, filters, onApply, onClear, onDone, renderRefValue, labelOfValue, idPrefix,
}: ColumnFilterEditorProps) {
  const ops = useMemo(() => operatorsFor(field), [field])
  const applied = useMemo(() => summarizeColumnFilter(field, filters, labelOfValue), [field, filters, labelOfValue])
  const decoded = useMemo(() => decodeColumnFilter(field, filters), [field, filters])

  /* Seeded from the view, so re-opening the menu on a filtered column shows the
     filter that is ON it rather than a blank form claiming there is none. A
     combination the editor cannot express (a preset) seeds a blank draft and
     leaves the existing leaves alone until Apply. */
  const [draft, setDraft] = useState<ColumnFilterDraft>(() => decoded ?? emptyDraft(field))
  const key = `${field.key}:${field.kind}`
  const seeded = useRef(key)
  useEffect(() => {
    if (seeded.current === key) return
    seeded.current = key
    setDraft(decoded ?? emptyDraft(field))
  }, [key, decoded, field])

  const spec = ops.find((o) => o.op === draft.op) ?? ops[0]
  const ready = draftIsComplete(draft, ops)
  const note = conservativeReason(field)
  const base = `colf-${(idPrefix ?? field.key).replace(/[^\w-]/g, '')}`
  const opId = `${base}-op`
  const valId = `${base}-val`
  const val2Id = `${base}-val2`

  const apply = () => {
    if (!ready) return
    onApply(encodeColumnFilter(field, draft))
    onDone?.()
  }
  const clear = () => { onClear(); onDone?.() }
  const onValueKey = (e: { key: string; preventDefault: () => void }) => {
    if (e.key === 'Enter') { e.preventDefault(); apply() }
  }

  /* One operand, rendered by what the OPERATOR needs — not by what the column
     is. A `between` on a date needs two date inputs; an `is any of` on a status
     needs the multi-select; a `ref` needs the host's lookup and never a uuid box. */
  const operand = (which: 'value' | 'value2', id: string, label: string): ReactNode => {
    if (!spec) return null
    const v = draft[which]
    const set = (next: unknown) => setDraft((d) => ({ ...d, [which]: next }))

    if (spec.input === 'ref') {
      return (
        <div className={s.operand}>
          <label className={s.lbl} htmlFor={id}>{label}</label>
          {renderRefValue
            ? renderRefValue({ value: String(v ?? ''), onChange: (refId) => set(refId), id })
            /* No picker supplied ⇒ say so rather than render a uuid box. A host
               that has not wired a lookup has not enabled ref filtering, and an
               input the user cannot fill correctly is worse than none. */
            : <span className={s.note} id={id}>This list has no picker for {field.label} yet.</span>}
        </div>
      )
    }

    if (spec.input === 'options') {
      return (
        <div className={s.operand}>
          <label className={s.lbl} htmlFor={id}>{label}</label>
          {/* `inline`: the open list lays out IN FLOW, above the action row,
              so it can never sit on top of "Apply filter" (#2375 repair). */}
          <MultiSelect id={id} aria-label={`${label} — ${field.label}`}
            value={(v as string[]) ?? []} options={field.options ?? []}
            onChange={(next) => set(next)} testid={`${base}-options`} listLayout="inline" />
        </div>
      )
    }

    if (spec.input === 'option' || spec.input === 'boolean') {
      const choices = spec.input === 'boolean'
        ? [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]
        : (field.options ?? [])
      return (
        <div className={s.operand}>
          <label className={s.lbl} htmlFor={id}>{label}</label>
          <select id={id} className={s.select} data-testid={`${base}-value`}
            value={String(v ?? '')}
            onChange={(e) => set(spec.input === 'boolean' ? e.target.value === 'true' : e.target.value)}>
            <option value="">Choose…</option>
            {choices.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )
    }

    /* `range` is the one operator whose operand type is not on the operator —
       `between` means two numbers on Amount and two dates on Due date. Reading
       it off `spec.input` alone gave a money column two DATE pickers, and typing
       a number into a date input does nothing at all: the bug looked like "Apply
       never enables". So the range case asks the FIELD. */
    const type = spec.input === 'range' ? rangeTypeFor(field.kind)
      : spec.input === 'number' ? 'number'
        : spec.input === 'datetime' ? 'datetime-local'
          : spec.input === 'date' ? dateTypeFor(field.kind)
            : 'text'
    if (type === 'date') return (
      <div className={s.operand}>
        <label className={s.lbl} htmlFor={id}>{label}</label>
        <DatePicker id={id} value={String(v ?? '')}
          data-testid={which === 'value' ? `${base}-value` : `${base}-value2`}
          onKeyDown={onValueKey} onChange={set} />
      </div>
    )
    return (
      <div className={s.operand}>
        <label className={s.lbl} htmlFor={id}>{label}</label>
        <Input id={id} type={type} value={String(v ?? '')} data-testid={which === 'value' ? `${base}-value` : `${base}-value2`}
          onKeyDown={onValueKey}
          onChange={(e) => set(type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} />
      </div>
    )
  }

  return (
    // A named group, so a screen reader entering the popover is told what this
    // block of controls is FOR before it reads the first label.
    <div className={s.wrap} role="group" aria-label={`Filter by ${field.label}`} data-testid={`${base}-editor`}>
      {/* The applied state, in words. `role="status"` so applying or clearing is
          ANNOUNCED — the header's mark is a tint, and a tint is not a message. */}
      <div role="status" className={s.applied} data-testid={`${base}-applied`}>
        {applied ? `Filtered: ${applied}` : 'No filter on this column'}
      </div>

      {spec ? (<>
      <div className={s.operand}>
        <label className={s.lbl} htmlFor={opId}>Operator</label>
        <select id={opId} className={s.select} data-testid={`${base}-op`}
          value={draft.op}
          onChange={(e) => {
            const next = ops.find((o) => o.op === e.target.value)!
            // The operand's TYPE changes with the operator, so a value carried
            // across would be the wrong shape — an array left behind by `is any
            // of` is not a number for `>`.
            setDraft({ op: next.op, value: next.input === 'options' ? [] : '' })
          }}>
          {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
        </select>
      </div>

      {spec.input === 'range'
        ? (<>{operand('value', valId, 'From')}{operand('value2', val2Id, 'To')}</>)
        : operand('value', valId, 'Value')}
      </>) : null}

      {/* No operator at all: the host cannot apply any filter to this column.
          Said, not drawn as an operator list that would only be refused. */}
      {note && <p className={s.note} data-testid={`${base}-note`}>{note}</p>}

      <div className={s.acts}>
        {spec && <Button type="button" size="sm" variant="primary" onClick={apply} disabled={!ready}
          data-testid={`${base}-apply`}>Apply filter</Button>}
        {applied && (
          <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid={`${base}-clear`}>
            Clear
          </Button>
        )}
      </div>
    </div>
  )
}

/** `date` takes a day picker; a `datetime` takes an instant. */
function dateTypeFor(kind: string): 'date' | 'datetime-local' {
  return kind === 'datetime' ? 'datetime-local' : 'date'
}

/** A `between`'s bounds are whatever the COLUMN is — two numbers, or two dates. */
function rangeTypeFor(kind: string): 'number' | 'date' | 'datetime-local' {
  if (kind === 'number' || kind === 'money' || kind === 'percent') return 'number'
  return dateTypeFor(kind)
}
